import React, { createContext, useContext, useState, useCallback } from 'react';

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  // Активный проект - загружаем из localStorage или используем 'shortly' по умолчанию
  const [projectId, setProjectIdState] = useState(() => {
    const saved = localStorage.getItem('dashboardProjectId');
    return saved || 'shortly';
  });
  
  // Обертка для setProjectId чтобы сохранять в localStorage
  const setProjectId = useCallback((newProjectId) => {
    setProjectIdState(newProjectId);
    localStorage.setItem('dashboardProjectId', newProjectId);
    
    // Сбрасываем кэш при смене проекта
    console.log('[DEBUG] Project changed - clearing section data');
    setSectionData({
      users: null,
      series: null,
      pulse: null
    });
  }, []);
  
  // Активный раздел - загружаем из localStorage или используем 'users' по умолчанию
  const [activeSection, setActiveSectionState] = useState(() => {
    const saved = localStorage.getItem('dashboardActiveSection');
    return saved || 'users';
  });
  
  // Обертка для setActiveSection чтобы сохранять в localStorage
  const setActiveSection = useCallback((section) => {
    setActiveSectionState(section);
    localStorage.setItem('dashboardActiveSection', section);
  }, []);
  
  // Даты фильтрации
  const [startDate, setStartDate] = useState(() => {
    const saved = localStorage.getItem('dashboardStartDate');
    return saved ? new Date(saved) : null;
  });
  
  const [endDate, setEndDate] = useState(() => {
    const saved = localStorage.getItem('dashboardEndDate');
    return saved ? new Date(saved) : null;
  });

  // Время фильтрации
  const [startTime, setStartTime] = useState(() => {
    const saved = localStorage.getItem('dashboardStartTime');
    return saved ? JSON.parse(saved) : { hours: 0, minutes: 0 };
  });
  
  const [endTime, setEndTime] = useState(() => {
    const saved = localStorage.getItem('dashboardEndTime');
    return saved ? JSON.parse(saved) : { hours: 23, minutes: 59 };
  });
  
  // Фильтр источника трафика
  const [trafficSource, setTrafficSource] = useState(() => {
    const saved = localStorage.getItem('dashboardTrafficSource');
    return saved ? JSON.parse(saved) : { campaign: 'all', campaigns: [], mediaSource: 'all' };
  });
  
  // Фильтр по ID пользователей
  const [userIdFilter, setUserIdFilter] = useState(() => {
    const saved = localStorage.getItem('dashboardUserIdFilter');
    return saved || 'all';
  });
  
  // Фильтр по источникам просмотра
  const [viewSourceFilter, setViewSourceFilter] = useState(() => {
    const saved = localStorage.getItem('dashboardViewSourceFilter');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Кэш загруженных данных
  const [sectionData, setSectionData] = useState({
    users: null,
    series: null,
    pulse: null
  });
  
  // Состояния загрузки для каждого раздела
  const [loading, setLoading] = useState({
    users: false,
    series: false,
    pulse: false
  });
  
  // Ошибки для каждого раздела
  const [errors, setErrors] = useState({
    users: null,
    series: null,
    pulse: null
  });

  // Обновление дат с сохранением в localStorage
  const updateDates = useCallback((newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    
    if (newStartDate) {
      localStorage.setItem('dashboardStartDate', newStartDate.toISOString());
    } else {
      localStorage.removeItem('dashboardStartDate');
    }
    
    if (newEndDate) {
      localStorage.setItem('dashboardEndDate', newEndDate.toISOString());
    } else {
      localStorage.removeItem('dashboardEndDate');
    }
    
    // ОТКЛЮЧЕНО ДЛЯ ОТЛАДКИ - всегда сбрасываем кэш
    console.log('[DEBUG] Client cache disabled - clearing section data');
    setSectionData({
      users: null,
      series: null,
      pulse: null
    });
  }, []);

  // Обновление времени с сохранением в localStorage
  const updateTimes = useCallback((newStartTime, newEndTime) => {
    if (newStartTime) {
      setStartTime(newStartTime);
      localStorage.setItem('dashboardStartTime', JSON.stringify(newStartTime));
    }
    
    if (newEndTime) {
      setEndTime(newEndTime);
      localStorage.setItem('dashboardEndTime', JSON.stringify(newEndTime));
    }
    
    // ОТКЛЮЧЕНО ДЛЯ ОТЛАДКИ - всегда сбрасываем кэш
    console.log('[DEBUG] Client cache disabled - clearing section data on time change');
    setSectionData({
      users: null,
      series: null,
      pulse: null
    });
  }, []);

  // Сброс дат и времени
  const resetDates = useCallback(() => {
    updateDates(null, null);
    setStartTime({ hours: 0, minutes: 0 });
    setEndTime({ hours: 23, minutes: 59 });
    localStorage.removeItem('dashboardStartTime');
    localStorage.removeItem('dashboardEndTime');
  }, [updateDates]);

  // Обновление фильтра источника трафика
  const updateTrafficSource = useCallback((newTrafficSource) => {
    setTrafficSource(newTrafficSource);
    localStorage.setItem('dashboardTrafficSource', JSON.stringify(newTrafficSource));
    
    // ОТКЛЮЧЕНО ДЛЯ ОТЛАДКИ - всегда сбрасываем кэш
    console.log('[DEBUG] Client cache disabled - clearing section data on traffic source change');
    setSectionData({
      users: null,
      series: null,
      pulse: null
    });
  }, []);

  // Обновление фильтра по ID пользователей
  const updateUserIdFilter = useCallback((newUserIdFilter) => {
    setUserIdFilter(newUserIdFilter);
    localStorage.setItem('dashboardUserIdFilter', newUserIdFilter);
    
    // ОТКЛЮЧЕНО ДЛЯ ОТЛАДКИ - всегда сбрасываем кэш
    console.log('[DEBUG] Client cache disabled - clearing section data on user ID filter change');
    setSectionData({
      users: null,
      series: null,
      pulse: null
    });
  }, []);

  // Обновление фильтра по источникам просмотра
  const updateViewSourceFilter = useCallback((newViewSourceFilter) => {
    setViewSourceFilter(newViewSourceFilter);
    if (newViewSourceFilter) {
      localStorage.setItem('dashboardViewSourceFilter', JSON.stringify(newViewSourceFilter));
    } else {
      localStorage.removeItem('dashboardViewSourceFilter');
    }
    
    // Сбрасываем кэш
    console.log('[DEBUG] Client cache disabled - clearing section data on view source filter change');
    setSectionData({
      users: null,
      series: null,
      pulse: null
    });
  }, []);

  // Установка данных для раздела
  const setSectionDataForSection = useCallback((section, data) => {
    // ОТКЛЮЧЕНО КЕШИРОВАНИЕ ДЛЯ ОТЛАДКИ - но данные все равно нужно сохранять для отображения
    console.log(`[DEBUG] Cache disabled - storing data for immediate use in section: ${section}`);
    setSectionData(prev => ({
      ...prev,
      [section]: data // Сохраняем данные для текущего отображения
    }));
  }, []);

  // Установка состояния загрузки для раздела
  const setLoadingForSection = useCallback((section, isLoading) => {
    setLoading(prev => ({
      ...prev,
      [section]: isLoading
    }));
  }, []);

  // Установка ошибки для раздела
  const setErrorForSection = useCallback((section, error) => {
    setErrors(prev => ({
      ...prev,
      [section]: error
    }));
  }, []);

  const value = {
    // Состояния
    projectId,
    activeSection,
    startDate,
    endDate,
    startTime,
    endTime,
    dateRange: { startDate, endDate, startTime, endTime }, // Добавляем объект dateRange для виджетов
    trafficSource,
    userIdFilter,
    viewSourceFilter,
    sectionData,
    loading,
    errors,
    
    // Методы
    setProjectId,
    setActiveSection,
    updateDates,
    updateTimes,
    resetDates,
    updateTrafficSource,
    updateUserIdFilter,
    updateViewSourceFilter,
    setSectionDataForSection,
    setLoadingForSection,
    setErrorForSection
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};
